import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_dummy');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#0f172a',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      '::placeholder': {
        color: '#94a3b8',
      },
    },
    invalid: {
      color: '#ff6b6b',
    },
  },
};

const PaymentForm = ({ gymData, onComplete, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Create payment method
      const cardElement = elements.getElement(CardElement);
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        return;
      }

      // Pass payment method to parent
      onComplete({ stripePaymentMethodId: paymentMethod.id });
    } catch (err) {
      setError(err.message || 'Payment setup failed');
      setProcessing(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 sm:p-8 border-2 border-slate-900 shadow-retro">
      <div className="flex items-center gap-2 mb-6">
        <span className="w-1 h-8 bg-gradient-to-b from-teal-500 to-purple-500 rounded"></span>
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Payment Setup</h2>
      </div>

      {/* Pricing Display */}
      <div className="bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 rounded-xl p-6 border-2 border-slate-900 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <span className="px-3 py-1 bg-sunshine-100 text-sunshine-800 border-2 border-sunshine-500 rounded-full font-bold text-sm">
              Founder Rate ⭐
            </span>
            <h3 className="text-3xl font-bold text-slate-900 mt-3">$49<span className="text-lg">/month</span></h3>
            <p className="text-sm text-slate-600 font-semibold mt-1">Locked in forever - never increases</p>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 border-2 border-slate-900">
          <div className="flex items-center gap-2 text-teal-600 mb-3">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-bold">First 30 days free</span>
          </div>
          <p className="text-sm text-slate-700">
            Then $49/month • Cancel anytime, no commitment
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">
            Card Information
          </label>
          <div className="px-4 py-4 border-2 border-slate-900 rounded-lg bg-white">
            <CardElement options={CARD_ELEMENT_OPTIONS} />
          </div>
          <p className="text-xs text-slate-600 mt-2 flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            Secured by Stripe • No charge for 30 days
          </p>
        </div>

        {error && (
          <div className="p-4 bg-coral-100 border-2 border-coral-500 rounded-lg">
            <p className="text-coral-800 font-semibold">{error}</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={processing}
            className="px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all touch-manipulation disabled:opacity-50"
          >
            ← Back
          </button>
          <button
            type="submit"
            disabled={!stripe || processing}
            className="flex-1 px-6 py-4 bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all touch-manipulation disabled:opacity-50"
          >
            {processing ? 'Processing...' : 'Complete Setup →'}
          </button>
        </div>
      </form>
    </div>
  );
};

const PaymentSetup = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm {...props} />
    </Elements>
  );
};

export default PaymentSetup;
